import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CalendarDays, CheckCircle, Clock, RefreshCw, Search, UserCheck, X } from 'lucide-react';
import MainService from '../../services/MainService';

const todayInput = () => new Date().toISOString().slice(0, 10);

const parseList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.Data)) return response.Data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const buildTimelineRequest = (employeeId, dateValue) => {
  const date = new Date(`${dateValue}T12:00:00`);
  return {
    EmployeeId: Number(employeeId),
    Month: date.getMonth() + 1,
    Day: date.getDate(),
    Year: date.getFullYear(),
  };
};

const EmployeeTimeline = () => {
  const [employees, setEmployees] = useState([]);
  const [filter, setFilter] = useState({ EmployeeId: '', Date: todayInput() });
  const [timelines, setTimelines] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const selectedEmployee = useMemo(
    () => employees.find((employee) => Number(employee.Id) === Number(filter.EmployeeId)),
    [employees, filter.EmployeeId]
  );

  const loadEmployees = useCallback(async () => {
    try {
      setEmployeesLoading(true);
      const response = await MainService.EmployeesInDropDown();
      setEmployees(parseList(response));
    } catch (err) {
      console.error('Load employees error:', err);
      setEmployees([]);
      setError('Failed to load employees');
    } finally {
      setEmployeesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  useEffect(() => {
    if (!error) return undefined;
    const timer = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(''), 3000);
    return () => clearTimeout(timer);
  }, [success]);

  const resetPage = () => {
    setFilter({ EmployeeId: '', Date: todayInput() });
    setTimelines([]);
    loadEmployees();
  };

  const getTimeline = async () => {
    if (!filter.EmployeeId) {
      setError('Select Employee');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await MainService.EmployeeTimeline(buildTimelineRequest(filter.EmployeeId, filter.Date));
      const list = parseList(response);
      setTimelines(list);
      if (list.length > 0) {
        setSuccess('Timeline loaded');
      }
    } catch (err) {
      console.error('Employee timeline error:', err);
      setTimelines([]);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {error && <Toast type="error" message={error} onClose={() => setError('')} />}
      {success && <Toast type="success" message={success} onClose={() => setSuccess('')} />}

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-6 flex flex-col gap-4 border-b border-gray-200 pb-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-3 text-primary">
              <Clock className="h-7 w-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Employee Timeline</h1>
              <p className="mt-1 text-sm text-gray-500">View employee timeline activity for a selected day.</p>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-5">
              <label className="mb-1 block text-sm font-medium text-gray-700">Employee</label>
              <select
                value={filter.EmployeeId}
                onChange={(e) => setFilter((prev) => ({ ...prev, EmployeeId: e.target.value }))}
                disabled={employeesLoading}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60"
              >
                <option value="">{employeesLoading ? 'Loading employees...' : 'Select Employee'}</option>
                {employees.map((employee) => (
                  <option key={employee.Id} value={employee.Id}>
                    {employee.Name || `Employee ${employee.Id}`}
                  </option>
                ))}
              </select>
            </div>
            <div className="lg:col-span-3">
              <label className="mb-1 block text-sm font-medium text-gray-700">Date</label>
              <input
                type="date"
                value={filter.Date}
                onChange={(e) => setFilter((prev) => ({ ...prev, Date: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div className="flex flex-wrap gap-2 lg:col-span-4">
              <button
                type="button"
                onClick={getTimeline}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
              >
                {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                Get Timeline
              </button>
              <button
                type="button"
                onClick={resetPage}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-5 flex flex-col gap-3 border-b border-gray-200 pb-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Timeline</h2>
            <p className="text-sm text-gray-500">
              {selectedEmployee ? selectedEmployee.Name : 'Select an employee'} · <CalendarDays className="inline h-4 w-4" /> {filter.Date}
            </p>
          </div>
          <div className="rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-600">
            {timelines.length} item(s)
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center text-gray-500">
            <RefreshCw className="mx-auto mb-2 h-8 w-8 animate-spin text-primary" />
            Loading timeline...
          </div>
        ) : timelines.length === 0 ? (
          <div className="py-16 text-center">
            <UserCheck className="mx-auto mb-3 h-10 w-10 text-gray-300" />
            <p className="text-sm text-gray-500">No Data To Display...</p>
          </div>
        ) : (
          <div className="relative mx-auto max-w-5xl py-2">
            <div className="absolute left-4 top-0 h-full w-0.5 bg-primary/20 md:left-1/2 md:-translate-x-1/2" />
            <div className="space-y-6">
              {timelines.map((item, index) => {
                const isLeft = index % 2 === 0;
                return (
                  <div key={item.Id || index} className={`relative flex ${isLeft ? 'md:justify-start' : 'md:justify-end'}`}>
                    <div className="absolute left-4 top-5 h-3 w-3 -translate-x-1/2 rounded-full bg-primary ring-4 ring-green-100 md:left-1/2" />
                    <div className={`ml-10 w-[calc(100%-2.5rem)] rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:ml-0 md:w-[46%] ${isLeft ? 'md:mr-auto' : 'md:ml-auto'}`}>
                      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-primary">
                        <Clock className="h-4 w-4" />
                        {formatDateTime(item.Date)}
                      </div>
                      <p className="whitespace-pre-wrap text-sm leading-6 text-gray-700">{item.Description || '-'}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Toast = ({ type, message, onClose }) => {
  const isError = type === 'error';
  return (
    <div className={`fixed right-4 top-4 z-50 flex min-w-[300px] items-center gap-3 rounded-lg border px-4 py-3 shadow-lg ${isError ? 'border-red-200 bg-red-50 text-red-700' : 'border-green-200 bg-green-50 text-green-700'}`}>
      {isError ? <AlertTriangle className="h-5 w-5 flex-shrink-0" /> : <CheckCircle className="h-5 w-5 flex-shrink-0" />}
      <span className="flex-1">{message}</span>
      <button type="button" onClick={onClose} className={isError ? 'text-red-500 hover:text-red-700' : 'text-green-500 hover:text-green-700'}>
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default EmployeeTimeline;
